import { createBrowserRouter, Navigate, Outlet } from 'react-router';

import { AuthProvider, useAuth } from './auth/AuthContext';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { landingAfterLogin } from './auth/session-provider';
import { AppLayout } from './components/AppLayout';
import { RouteErrorFallback } from './components/RouteErrorFallback';
import { UsedeskWidget } from './components/UsedeskWidget';
import { ApplicationProfilePage } from './pages/applications/ApplicationProfilePage';
import { ApplicationsPage } from './pages/applications/ApplicationsPage';
import { CabinetPage } from './pages/cabinet/CabinetPage';
import { GroupsPage } from './pages/cabinet/GroupsPage';
import { VenuePrefillPage } from './pages/cabinet/VenuePrefillPage';
import { CuratorDashboardPage, MyDashboardPage } from './pages/curator-dashboard/CuratorDashboardPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { LoginPage } from './pages/login/LoginPage';
import { MailingsPage } from './pages/mailings/MailingsPage';
import { LoggedOutPage } from './pages/LoggedOutPage';
import { QuotasPage } from './pages/quotas/QuotasPage';
import { CaDistributionPage } from './pages/ca-distribution/CaDistributionPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { TeachersPage } from './pages/teachers/TeachersPage';
import { UntiApplicationsPage } from './pages/unti/UntiApplicationsPage';
import { VenuesPage } from './pages/venues/VenuesPage';

/**
 * react-router v7, library mode.
 *
 * Outside the shell: `/login` (sign-in + error states) and `/logged-out` (the
 * signed-out interstitial). A visitor without a session must never see an app
 * frame — it would imply they are still in.
 *
 * The shell's app routes sit behind `<ProtectedRoute>`: the guard reads the
 * session the ONE root `<AuthProvider>` resolved once (`GET /auth/me`, cached)
 * and bounces anonymous visitors to `/login?next=…`. `AUTH_DISABLED` (dev / e2e)
 * passes straight through, which is what keeps the local loop free of a login
 * dance. Since T.Z116 the provider and the UseDesk widget live in the pathless
 * root route ABOVE all of this, so the pre-auth screens have a session answer —
 * and an (anonymous) support chat — too.
 */

/**
 * T.Z85 (owner, 2026-09-16) — the start page, BY ROLE. Every path that ends on
 * `/` without the SPA having chosen it lands here: a hand-typed or bookmarked
 * address, and the api's OAuth callback, which redirects EVERYONE to `/` (the
 * Yandex door has no role knowledge server-side). A КУРАТОР-role session is
 * redirected to `/cabinet` BEFORE the dashboard mounts — so the `GET /dashboard`
 * that answered 403 `CURATOR_SCOPE_REQUIRED` as «Что-то пошло не так» is never
 * even sent. Staff sessions render the dashboard exactly as before.
 *
 * The decision is `landingAfterLogin` — the one Z60 predicate in
 * `./auth/session-provider`, the same one the post-verify hop uses — so the two
 * layers cannot drift. Under `<ProtectedRoute>` the session is already resolved
 * here (loading and anonymous never reach the children); `AUTH_DISABLED` (dev /
 * e2e) reads as staff and keeps today's behavior.
 */
function HomeRoute() {
  const { role } = useAuth();
  const landing = landingAfterLogin(role);
  return landing === '/cabinet' ? <Navigate replace to={landing} /> : <DashboardPage />;
}

/**
 * The ONE root above every route (T.Z116): `<AuthProvider>` resolves the session
 * for the whole tree — the shell, `/login` and `/logged-out` alike — and
 * `<UsedeskWidget>` hangs off it, so the support chat exists on the
 * unauthenticated surface too (anonymously — see the component; `/auth/me`
 * answering 401 there is its documented "not signed in" answer, never a bounce:
 * `handleUnauthorized` skips every `/auth` path). A visitor who cannot sign in
 * is exactly the visitor who needs support.
 *
 * T.Z122 — the root carries `errorElement`: a render error anywhere in the tree
 * (no route below defines one, so every error bubbles here) renders
 * `<RouteErrorFallback>` — a RU card with a reload button — instead of
 * unmounting the SPA to a white screen.
 */
export const router = createBrowserRouter([
  {
    element: (
      <AuthProvider>
        <UsedeskWidget />
        <Outlet />
      </AuthProvider>
    ),
    errorElement: <RouteErrorFallback />,
    children: [
      { path: '/login', element: <LoginPage /> },
      {
        path: '/',
        element: (
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        ),
        children: [
        { index: true, element: <HomeRoute /> },
        // DATAANALYTICS-112 — «Дашборд для кураторов», the check stage: the frame of the Future Monitoring app. The page
        // itself admits exactly ADMIN (`minRole` also opens for SUPPORT).
        {
          path: 'curator-dashboard',
          element: (
            <ProtectedRoute minRole="ADMIN">
              <CuratorDashboardPage />
            </ProtectedRoute>
          ),
        },
        // DATAANALYTICS-112 — the curator's own «Дашборд» (owner 03.11): the route only asks for a session, like
        // `/cabinet`; the api decides what the account opens. The nav offers it to a КУРАТОР-role session only.
        { path: 'my-dashboard', element: <MyDashboardPage /> },
        { path: 'applications', element: <ApplicationsPage /> },
        { path: 'applications/:pguId', element: <ApplicationProfilePage /> },
        // T.Y8 — the curator cabinet (wave 9, SR §5.2). The ROUTE only asks for a
        // session, on purpose: the contract lets ADMIN look through #26 too, and the
        // api is the one that refuses (403 CURATOR_SCOPE_REQUIRED) a session with no
        // area. Who is OFFERED the item is the nav's business (`canSeeCabinet`).
        { path: 'cabinet', element: <CabinetPage /> },
        // T.Z60 — the «Группы» page: the study groups moved out of the cabinet onto
        // their own surface (`GroupsPage` over #26.8–#26.13). The ROUTE only asks for a
        // session, on purpose — the same arrangement `/cabinet` and `/teachers` keep:
        // who is OFFERED the item is the nav's business (`canSeeGroups` — a КУРАТОР-role
        // session only), and the api is the one that answers a venue outside the
        // session's cabinet scope with 403.
        { path: 'groups', element: <GroupsPage /> },
        { path: 'venues', element: <VenuesPage /> },
        // T.Z27 — the venue card's «Онлайн-набор»: the cabinet online block scoped to
        // ONE venue, for a МЕНЕДЖЕР linked to a flagged venue (and an АДМИН over its
        // link union). The route asks for a МЕНЕДЖЕР session only — the api is still
        // the one that answers a venue outside the session's cabinet scope with 403,
        // and the page renders that refusal as its own named state.
        {
          path: 'venues/:venueId/prefill',
          element: (
            <ProtectedRoute minRole="MANAGER">
              <VenuePrefillPage />
            </ProtectedRoute>
          ),
        },
        // T.W1 — the «Распределение по ЦА» pivot. VIEWER like the roster it reads: counts
        // and master venue names only, no PII, no write.
        { path: 'ca-distribution', element: <CaDistributionPage /> },
        // T.X2 (owner): teachers, quotas and settings are the ADMIN screens. The nav does
        // not offer them to any other role, and the guard is what a hand-typed URL meets —
        // the same 403 state the api's own refusal would render.
        {
          path: 'quotas',
          element: (
            <ProtectedRoute minRole="ADMIN">
              <QuotasPage />
            </ProtectedRoute>
          ),
        },
        // T.U2 — «Данные УНТИ»: the fourth admin screen, placed below «Квоты» in
        // the nav (owner brief). The ADMIN floor is the api's own (#36/#37 carry
        // `x-contract-role: ADMIN` — the mirror reads pgu_id); the guard here is
        // what a hand-typed URL meets, exactly like /quotas.
        {
          path: 'unti',
          element: (
            <ProtectedRoute minRole="ADMIN">
              <UntiApplicationsPage />
            </ProtectedRoute>
          ),
        },
        // T.F6 — the teacher registry. `/teachers?venueId=N` is the venue card's
        // «Педагоги» deep link; the page reads the param as its roster filter.
        // ADDITIVE (v1.9.5, T.Z52): the route no longer demands ADMIN — a КУРАТОР-role
        // session is offered the page too, and the API is the one that scopes its read
        // to that curator's own venues (`ExternalAccess('scoped')` on #7.5) and keeps
        // the writes MANAGER+/ADMIN, so the page renders read-only for them. The same
        // arrangement the cabinet route uses: the router only asks for a session, the
        // api is the boundary.
        { path: 'teachers', element: <TeachersPage /> },
        {
          path: 'settings',
          element: (
            <ProtectedRoute minRole="ADMIN">
              <SettingsPage />
            </ProtectedRoute>
          ),
        },
        // T.Z124 — the «Рассылки» staff surface: MANAGER+ by the ROUTE (the api's
        // own `@Roles(MANAGER)` is the boundary), placed below «Квоты» in the nav.
        {
          path: 'mailings',
          element: (
            <ProtectedRoute minRole="MANAGER">
              <MailingsPage />
            </ProtectedRoute>
          ),
        },
        ],
      },
      // T.Z116 — the widget covers the signed-out interstitial too (same root, same
      // anonymous boot). Logout is a full page navigation through `/auth/logout`,
      // which is what actually ends the UseDesk attribution of the old session.
      { path: '/logged-out', element: <LoggedOutPage /> },
    ],
  },
]);
